import { ToastType } from '../hooks/useToast';

/**
 * Custom error class for application-wide error handling
 */
export class ApplicationError extends Error {
  public originalError: any;
  public code?: string;
  public context?: Record<string, any>;

  constructor(
    originalError: any, 
    code?: string, 
    context?: Record<string, any>
  ) {
    super(originalError?.message || 'An unexpected error occurred');
    this.name = 'ApplicationError';
    this.originalError = originalError;
    this.code = code;
    this.context = context;
  }
}

/**
 * Parse and standardize error messages for user-friendly display
 * @param error Error object to parse
 * @returns Standardized error message and type
 */
export function parseError(error: any): { message: string, type: ToastType } {
  // Firebase Authentication Errors
  if (error?.code?.startsWith('auth/')) {
    switch (error.code) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        return {
          message: 'Invalid email or password',
          type: 'error'
        };
      case 'auth/email-already-in-use':
        return {
          message: 'An account with this email already exists',
          type: 'error'
        };
      case 'auth/weak-password':
        return {
          message: 'Password should be at least 6 characters',
          type: 'warning'
        };
      case 'auth/invalid-email':
        return {
          message: 'Please enter a valid email address',
          type: 'warning'
        };
      case 'auth/too-many-requests':
        return {
          message: 'Too many attempts. Please try again later',
          type: 'error'
        };
      default:
        return {
          message: import.meta.env.DEV ? error.message : 'Authentication error occurred',
          type: 'error'
        };
    }
  }

  // Firestore Errors
  if (error?.code?.startsWith('firestore/')) {
    switch (error.code) {
      case 'firestore/permission-denied':
        return {
          message: 'You do not have permission to perform this action',
          type: 'error'
        };
      case 'firestore/not-found':
        return {
          message: 'The requested resource was not found',
          type: 'warning'
        };
      default:
        return {
          message: import.meta.env.DEV ? error.message : 'Database operation failed',
          type: 'error'
        };
    }
  }

  // Generic Application Errors
  switch (error?.code) {
    case 'permission-denied':
      return {
        message: 'You do not have permission to perform this action',
        type: 'error'
      };
    case 'not-found':
      return {
        message: 'The requested resource was not found',
        type: 'warning'
      };
    case 'unauthenticated':
      return {
        message: 'Authentication is required',
        type: 'error'
      };
    default:
      return {
        message: import.meta.env.DEV ? error?.message : 'An unexpected error occurred',
        type: 'error'
      };
  }
}

/**
 * Log errors with context and environment-specific details
 * @param error Error to log
 * @param context Additional context for the error
 */
export function logError(error: Error, context?: Record<string, any>): void {
  const errorLog = {
    message: error.message,
    name: error.name,
    code: (error as any)?.code,
    context: {
      ...context,
      // Remove sensitive data in production
      ...(import.meta.env.PROD ? {} : { stack: error.stack })
    },
    timestamp: new Date().toISOString(),
    environment: import.meta.env.MODE
  };

  if (import.meta.env.PROD) {
    // In production, only log error code and sanitized message
    console.error(JSON.stringify({
      code: errorLog.code,
      message: parseError(error).message,
      timestamp: errorLog.timestamp
    }));
  } else {
    // In development, log full error details
    console.error('Error details:', errorLog);
    if (error.stack) {
      console.error('Stack trace:', error.stack);
    }
  }

  // TODO: Integrate with external error tracking service (e.g., Sentry)
  // Sentry.captureException(error);
}
